// ============================================================
// Where a floating preview goes - in VIEWPORT coordinates.
//
// It used to be `position: absolute; left: calc(100% + 8px)` on the row
// it describes. Two things went wrong with that, and they compounded:
//
// The character panel is `overflow-y: auto`, and CSS will not give you
// one axis scrollable and the other visible - `overflow-x` computes to
// `auto` as well. An absolutely positioned child therefore counts
// towards the panel's SCROLLABLE OVERFLOW. With a single character the
// panel is short and has no scrollbars, so hovering conjured a preview,
// the scrollbars appeared, the content box shrank, the row reflowed out
// from under the pointer, `mouseleave` fired, the preview vanished, the
// scrollbars went with it, and the row came back under the pointer. The
// menu trembled at whatever rate the browser could manage (v8.23.1).
//
// And the panel hugs the right edge of the window, so "just past my
// right edge" was off-screen to begin with.
//
// `position: fixed` takes it out of the scroll container's overflow
// entirely - its containing block is the viewport - which is what makes
// the loop impossible rather than merely unlikely.
// ============================================================

export interface Rect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Placement {
  top: number;
  left: number;
}

const GAP = 8;
const EDGE = 4;

/**
 * Beside `anchor`, inside the viewport.
 *
 * PREFERS THE LEFT, because the thing this describes lives in a panel
 * pinned to the right-hand edge; the right is tried only when the left
 * cannot hold it. Vertically it starts level with the row and is pushed
 * back inside if it would hang off the bottom - a preview whose lower
 * half is unreachable is no preview at all.
 */
export function previewPlacement(anchor: Rect, size: Size, viewport: Viewport): Placement {
  const fitsLeft = anchor.left - GAP - size.width >= EDGE;
  const left = fitsLeft
    ? anchor.left - GAP - size.width
    : Math.min(anchor.right + GAP, Math.max(EDGE, viewport.width - size.width - EDGE));

  const top = Math.max(EDGE, Math.min(anchor.top, viewport.height - size.height - EDGE));
  return { top, left };
}
